import { createFileRoute } from "@tanstack/react-router";
import { useAtomValue } from "jotai";

import { graphql } from "@repo/graphql-types/gql";
import Left from "@repo/icons/default/Left";

import { ReservedScheduleLinkedScheduleForm } from "@/components/reserved-schedules/reserved-schedule-linked-schedule-form";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { useGraphQL } from "@/hooks/use-graphql";
import { reservedScheduleFormAtom } from "@/lib/atoms/reserved-schedule-form-atom";
import { requireLinkedScheduleStep } from "@/lib/reserved-schedules/linked-schedule-step";
import { AppointmentTypeModalityEnum } from "@/lib/enums/appointment-type-modality-enum";
import { requireChosenModality } from "@/lib/reserved-schedules/require-chosen-modality";

import type { JSX } from "react";

/**
 * Os tipos de atendimento que a agenda vinculada pode oferecer: os **reais** do profissional, sem a
 * categoria 3 (Período Garantido) — o mesmo filtro do formulário de agenda (`schedule-form.tsx`) —
 * e **só os presenciais**. A agenda nasce com a unidade do PG, que é sala física; um tipo de
 * teleconsulta nela não é agendável — só faz sentido no Consultório Virtual.
 */
const GetReservedScheduleLinkedScheduleQuery = graphql(/* GraphQL */ `
  query GetReservedScheduleLinkedScheduleQuery(
    $codUsuario: Int!
    $codModalidadeTeleconsulta: Int!
  ) {
    usuariosCompromissos(
      where: {
        ativo: { _eq: true }
        codUsuario: { _eq: $codUsuario }
        categoria: { _neq: 3 }
        codUsuarioCompromissoModalidade: { _neq: $codModalidadeTeleconsulta }
      }
      order_by: { nome: asc }
    ) {
      ...AppointmentTypesListToMultiSelectQueryFragment
    }
  }
`);

const ReservedScheduleLinkedSchedulePage = (): JSX.Element => {
  const reservedScheduleAtom = useAtomValue(reservedScheduleFormAtom);
  const { user } = Route.useRouteContext();
  const queryResult = useGraphQL(GetReservedScheduleLinkedScheduleQuery, {
    codUsuario: user.codUsuario,
    codModalidadeTeleconsulta: AppointmentTypeModalityEnum.Teleconsulta,
  });

  const { data } = queryResult;

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Novo período garantido" align="center" />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryResult}>
          {data ? (
            <ReservedScheduleLinkedScheduleForm
              appointmentTypesFragment={data.usuariosCompromissos}
              frequency={reservedScheduleAtom.frequency}
            />
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute(
  "/settings/reserved-schedules/create/linked-schedule",
)({
  component: ReservedScheduleLinkedSchedulePage,
  loader: ({ context }) => {
    requireChosenModality();
    requireLinkedScheduleStep(context.flags);
  },
});
